import { Calendar, Coins, FileText, User, Users, X, Loader2, CheckCheck, CalendarIcon, Eye } from 'lucide-react'
import { format, isValid } from 'date-fns'
import { pt, ptBR } from 'date-fns/locale'
import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/modules/shared/components/ui/card'
import { Button } from '@/modules/shared/components/ui/button'
import { Input } from '@/modules/shared/components/ui/input'
import { Textarea } from '@/modules/shared/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/modules/shared/components/ui/dialog'
import { ScrollArea } from '@/modules/shared/components/ui/scroll-area'
import { Label } from '@/modules/shared/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/modules/shared/components/ui/popover'
import { Calendar as CalendarComponent } from '@/modules/shared/components/ui/calendar'
import { CustomerSearchInputComponent } from '@/modules/shared/components/client-search-input.component'
import type { Entity } from '@/modules/shared/types/entity.type'
import { cn } from '@/modules/shared/lib/utils'
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from '@/modules/shared/components/ui/alert-dialog'
import { usePromotionVisitForm } from '../hooks/use-promotion-visit-form.hook'
import { formatCnpjCpf } from '@/modules/customer/helpers/format-fields-customer-form.helper'

type PromotionVisitFormInterfaceProps = ReturnType<typeof usePromotionVisitForm>
const formatToBRL = (value: number | undefined): string => {
	if (!value && value !== 0) return ''

	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(value)
}

const parseBRLToNumber = (value: string): number => {
	if (!value) return 0

	const cleanValue = value.replace(/[^\d,]/g, '')
	const numberValue = cleanValue.replace(',', '.')
	return parseFloat(numberValue) || 0
}

const formatCurrencyInput = (value: string): string => {
	const numericValue = value.replace(/\D/g, '')

	if (!numericValue) return ''

	const numberValue = parseInt(numericValue) / 100

	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(numberValue)
}

export function PromotionVisitFormInterface(props: PromotionVisitFormInterfaceProps) {
	const {
		promotorData,
		valor,
		setValor,
		observacao,
		setObservacao,
		visitDate,
		setVisitDate,
		selectedClients,
		dataInclusao,
		openModal,
		setOpenModal,
		promoters,
		isSubmitting,
		loadError,
		isLoadingVisit,
		customers,
		dataLoaded,
		isLoadingMore = false,
		hasMorePromoters = false,
		hasMoreCustomers = false,
		totalRecordsPromoters = 0,
		totalRecordsCustomers = 0,
		isLoadingEntities,
		loadMorePromoters,
		loadMoreCustomers,
		isEditMode,
		isViewOnly,
		handleSearchPromoter,
		handleSearchClient,
		handleSelectPromoter,
		handleSelectClient,
		handleRemoveClient,
		handleRemovePromoter,
		handleCloseModal,
		handleSubmit,
		navigateBack
	} = props

	const loadingRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const currentModal = openModal
		const hasMore = currentModal === 'promoter' ? hasMorePromoters : hasMoreCustomers
		const loadMore = currentModal === 'promoter' ? loadMorePromoters : loadMoreCustomers
		const entities = currentModal === 'promoter' ? promoters : customers

		if (!hasMore || isLoadingMore || !entities || entities.length === 0 || !loadMore) return

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
					loadMore()
				}
			},
			{ threshold: 0.1 }
		)

		if (loadingRef.current) {
			observer.observe(loadingRef.current)
		}

		return () => observer.disconnect()
	}, [openModal, hasMorePromoters, hasMoreCustomers, isLoadingMore, loadMorePromoters, loadMoreCustomers, promoters, customers])

	const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value
		const formattedValue = formatCurrencyInput(inputValue)
		e.target.value = formattedValue
		const numericValue = parseBRLToNumber(formattedValue)
		setValor(numericValue)
	}

	const getCurrentModalData = () => {
		if (openModal === 'promoter') {
			return {
				entities: promoters,
				hasMore: hasMorePromoters,
				totalRecords: totalRecordsPromoters,
				loadMore: loadMorePromoters,
				title: 'Selecione o promotor',
				searchHandler: handleSearchPromoter,
				selectHandler: handleSelectPromoter,
				emptyMessage: 'Nenhum promotor encontrado',
				loadingMessage: 'Carregando promotores...'
			}
		} else {
			return {
				entities: customers,
				hasMore: hasMoreCustomers,
				totalRecords: totalRecordsCustomers,
				loadMore: loadMoreCustomers,
				title: 'Selecione os clientes',
				searchHandler: handleSearchClient,
				selectHandler: handleSelectClient,
				emptyMessage: 'Nenhum cliente encontrado',
				loadingMessage: 'Carregando clientes...'
			}
		}
	}

	const modalData = getCurrentModalData()

	const getCounterText = () => {
		if (!modalData.entities || modalData.entities.length === 0) return ''

		if (modalData.totalRecords > 0) {
			const currentCount = modalData.entities.length
			const isComplete = !modalData.hasMore

			if (isComplete) {
				return modalData.totalRecords === 1
					? `${modalData.totalRecords} ${openModal === 'promoter' ? 'promotor' : 'cliente'} encontrado`
					: `${modalData.totalRecords} ${openModal === 'promoter' ? 'promotores' : 'clientes'} encontrados`
			} else {
				return `${currentCount} de ${modalData.totalRecords} ${openModal === 'promoter' ? 'promotores' : 'clientes'}`
			}
		}

		return modalData.entities.length === 1
			? `${modalData.entities.length} ${openModal === 'promoter' ? 'promotor' : 'cliente'} encontrado`
			: `${modalData.entities.length} ${openModal === 'promoter' ? 'promotores' : 'clientes'} encontrados`
	}

	// Get percentage loaded for the progress indicator
	const getLoadPercentage = () => {
		if (!modalData.totalRecords || modalData.totalRecords === 0 || !modalData.entities) return 0
		return Math.round((modalData.entities.length / modalData.totalRecords) * 100)
	}

	if (isLoadingVisit && !dataLoaded) {
		return (
			<AlertDialog open={true}>
				<AlertDialogContent className="flex flex-col items-center flex-1 top-[95%] m-0 pb-28 md:pb-40 md:pt-10 lg:p-8 w-screen max-w-full lg:max-w-lg mb-2 lg:top-[50%] rounded-t-2xl">
					<Loader2 className="animate-spin text-primary size-10 text-center" />
					<AlertDialogTitle className='hidden'>Carregando dados da visita</AlertDialogTitle>
					<AlertDialogDescription className='text-center mb-4'>Aguarde enquanto os dados da visita estão sendo carregados...</AlertDialogDescription>
				</AlertDialogContent>
			</AlertDialog>
		)
	}

	if (loadError && isEditMode) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen p-4">
				<div className="text-red-500 mb-4">
					<p>Erro ao carregar dados da visita</p>
					<p className="text-sm mt-2">{loadError}</p>
				</div>
				<Button onClick={navigateBack}>Voltar</Button>
			</div>
		)
	}

	return (
		<div className="min-h-screen pb-20">
			<div className="mx-auto">
				<Card>
					<CardHeader>
						<CardTitle className="text-lg flex items-center">
							{isViewOnly ? (
								<>
									<Eye className="mr-2 h-5 w-5" />
									Visualizar visita
								</>
							) : isEditMode ? (
								<>
									<Calendar className="mr-2 h-5 w-5" />
									Editar visita
								</>
							) : (
								<>
									<Calendar className="mr-2 h-5 w-5" />
									Nova visita
								</>
							)}
						</CardTitle>
						{!isViewOnly && (
							<div className="flex text-xs">
								<p>Dados obrigatórios</p>
								<p className="text-red-500">(*)</p>
							</div>
						)}
					</CardHeader>
					<CardContent>
						<form onSubmit={handleSubmit}>
							<div className="space-y-6">
								<div className="space-y-3">
									<h2 className="text-md font-bold flex items-center">
										<User className="mr-2 h-4 w-4" />
										Promotor
									</h2>

									<div className="space-y-2">
										<Label className="flex font-normal">
											Promotor selecionado
											{!isViewOnly && <span className="text-red-500 ml-1">*</span>}
										</Label>

										{promotorData ? (
											<Card className={cn("bg-muted", isViewOnly ? "" : "cursor-pointer hover:bg-muted/20")}>
												<CardHeader className="p-2 -mb-4">
													<CardTitle className="text-sm text-balance flex justify-between items-start">
														<span className='text-xs'>{promotorData.idEntidade} - {promotorData.razaoSocialNome}</span>
														{!isViewOnly && (
															<Button
																type="button"
																variant="ghost"
																size="sm"
																className="h-6 w-6 p-0 hover:bg-red-100"
																onClick={handleRemovePromoter}
															>
																<X className="h-4 w-4" />
															</Button>
														)}
													</CardTitle>
												</CardHeader>
												<CardContent className="p-3">
													<p className="text-xs">{formatCnpjCpf(promotorData.cnpjCpf) || 'CPF/CNPJ não disponível'}</p>
													{promotorData.logradouro && (
														<>
															<p className="text-xs">
																{promotorData.logradouro}, {promotorData.numero || 'S/N'}
															</p>
															<p className="text-xs">
																{promotorData.cidade || 'Cidade não informada'}, {promotorData.uf || 'UF'}
															</p>
														</>
													)}
												</CardContent>
											</Card>
										) : !isViewOnly ? (
											<Button
												type="button"
												variant="outline"
												className="w-full h-20 border-dashed"
												onClick={() => setOpenModal('promoter')}
											>
												<div className="text-center">
													<User className="h-6 w-6 mx-auto mb-1" />
													<p className="text-sm">Clique para selecionar um promotor</p>
												</div>
											</Button>
										) : (
											<div className="border border-dashed rounded-md p-4 text-center text-gray-500">
												<User className="h-6 w-6 mx-auto mb-2 opacity-50" />
												<p className="text-sm">Nenhum promotor disponível</p>
											</div>
										)}
									</div>
								</div>

								<div className="space-y-3">
									<h2 className="text-md font-bold flex items-center">
										<Users className="mr-2 h-4 w-4" />
										Clientes
									</h2>

									<div className="space-y-2">
										<Label className="flex font-normal">
											Clientes selecionados
											{!isViewOnly && <span className="text-red-500 ml-1">*</span>}
										</Label>

										{!isViewOnly && (
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={() => setOpenModal('client')}
											>
												<Users className="h-4 w-4 mr-2" />
												Adicionar Clientes
											</Button>
										)}

										{selectedClients.length > 0 && (
											<div className="grid gap-2 mt-3">
												{selectedClients.map((client) => (
													<div key={client.idEntidade} className="py-2 px-3 border rounded-md flex justify-between">
														<div className='flex flex-col'>
															<span className='font-medium text-xs'>{client.razaoSocialNome}</span>
															<span className='text-xs'>{formatCnpjCpf(client.cnpjCpf) || 'CPF/CNPJ não disponível'}</span>
														</div>
														{!isViewOnly && (
															<Button
																type="button"
																variant="ghost"
																size="icon"
																className="h-4 w-4 ml-2 p-0 hover:bg-red-100"
																onClick={() => handleRemoveClient(client.idEntidade)}
															>
																<X className="h-3 w-3" />
															</Button>
														)}
													</div>
												))}
											</div>
										)}

										{selectedClients.length === 0 && (
											<div className="border border-dashed rounded-md p-4 text-center text-gray-500">
												<Users className="h-6 w-6 mx-auto mb-2 opacity-50" />
												<p className="text-sm">Nenhum cliente selecionado</p>
											</div>
										)}
									</div>
								</div>

								<div className="space-y-3">
									<h2 className="text-md font-bold flex items-center">
										<Calendar className="mr-2 h-4 w-4" />
										Data da Visita
									</h2>
									<div className="space-y-2">
										<Label className="flex font-normal">
											Data
											{!isViewOnly && <span className="text-red-500 ml-1">*</span>}
										</Label>
										{isViewOnly ? (
											<div className="p-2 border rounded-md">
												{visitDate && isValid(visitDate) ? (
													format(visitDate, "dd/MM/yyyy", { locale: ptBR })
												) : (
													<span className="text-gray-500">Data não disponível</span>
												)}
											</div>
										) : (
											<Popover>
												<PopoverTrigger asChild>
													<Button
														variant="outline"
														className={cn(
															"w-full justify-start text-left font-normal",
															!visitDate && "text-muted-foreground"
														)}
													>
														<CalendarIcon className="mr-2 h-4 w-4" />
														{visitDate && isValid(visitDate) ? (
															format(visitDate, "dd/MM/yyyy", { locale: pt })
														) : (
															<span>Selecione uma data</span>
														)}
													</Button>
												</PopoverTrigger>
												<PopoverContent className="w-auto p-0" align="start">
													<CalendarComponent
														mode="single"
														selected={visitDate}
														onSelect={setVisitDate}
														initialFocus
														locale={pt}
													/>
												</PopoverContent>
											</Popover>
										)}
									</div>
								</div>

								<div className="space-y-3">
									<h2 className="text-md font-bold flex items-center">
										<Coins className="mr-2 h-4 w-4" />
										Valor
									</h2>
									<div className="space-y-2">
										<Label>Valor da visita</Label>
										{isViewOnly ? (
											<div className="p-2 border rounded-md">
												{valor !== undefined ? formatToBRL(valor) : 'Não informado'}
											</div>
										) : (
											<Input
												type="text"
												placeholder="R$ 0,00"
												defaultValue={valor ? formatToBRL(valor) : ''}
												onChange={handleValorChange}
												className="text-left"
											/>
										)}
									</div>
								</div>

								<div className="space-y-3">
									<h2 className="text-md font-bold flex items-center">
										<FileText className="mr-2 h-4 w-4" />
										Observação
									</h2>
									<div className="space-y-2">
										<Label>Observações</Label>
										{isViewOnly ? (
											<div className="p-2 border rounded-md min-h-24">
												{observacao || <span className="text-gray-500">Sem observações</span>}
											</div>
										) : (
											<Textarea
												placeholder="Digite uma observação"
												className="min-h-24"
												value={observacao}
												onChange={(e) => setObservacao(e.target.value)}
											/>
										)}
									</div>
								</div>

								{isViewOnly && dataInclusao && (
									<div className="space-y-3">
										<h2 className="text-md font-bold flex items-center">
											<Calendar className="mr-2 h-4 w-4" />
											Data de Inclusão
										</h2>
										<div className="space-y-2">
											<div className="p-2 border rounded-md">
												{dayjs(dataInclusao).format('DD/MM/YYYY HH:mm')}
											</div>
										</div>
									</div>
								)}

								<div className="flex justify-end pt-6 gap-2">
									<Button
										type="button"
										variant="outline"
										onClick={navigateBack}
									>
										{isViewOnly ? 'Voltar' : 'Cancelar'}
									</Button>
									{!isViewOnly && (
										<Button
											type="submit"
											disabled={isSubmitting}
											className="bg-green-500 hover:bg-green-600"
										>
											{isSubmitting ? (
												<>
													<Loader2 className="h-4 w-4 mr-2 animate-spin text-white" />
													Salvando...
												</>
											) : (
												<>
													<CheckCheck className="h-4 w-4 mr-2" />
													{isEditMode ? 'Atualizar' : 'Salvar'}
												</>
											)}
										</Button>
									)}
								</div>
							</div>
						</form>
					</CardContent>
				</Card>

				{!isViewOnly && (
					<Dialog open={!!openModal} onOpenChange={handleCloseModal}>
						<DialogContent className="bg-white max-w-2xl">
							<DialogHeader>
								<DialogTitle>{modalData.title}</DialogTitle>
								{/* Enhanced counter display with percentage loaded */}
								{modalData.entities?.length > 0 && (
									<div className="flex flex-col space-y-1">
										<div className="flex items-center gap-2">
											<span className="text-sm text-muted-foreground  px-2 py-1 rounded-full">
												{getCounterText()}
											</span>
										</div>
									</div>
								)}
							</DialogHeader>

							<CustomerSearchInputComponent
								onSubmitRequest={modalData.searchHandler}
							/>

							<ScrollArea>
								<div className="h-[70vh] space-y-2">
									{isLoadingEntities ? (
										<div className="justify-center items-center h-full flex flex-col">
											<Loader2 size={32} className="animate-spin text-primary" />
											<p className="pt-6">{modalData.loadingMessage}</p>
										</div>
									) : modalData.entities?.length > 0 ? (
										<>
											{modalData.entities.map((entity, index) => (
												<Card
													key={`${entity?.idEntidade}-${index}`}
													className={cn(
														'cursor-pointer hover:bg-muted/20 transition-colors',
														openModal === 'client' &&
															selectedClients.some(c => c.idEntidade === entity.idEntidade)
															? 'bg-blue-50 border-blue-300'
															: 'bg-muted'
													)}
													onClick={() => modalData.selectHandler(entity)}
												>
													<CardHeader className="p-3 -mb-4">
														<CardTitle className="text-sm text-balance flex justify-between items-start">
															<span>{entity?.idEntidade} - {entity?.razaoSocialNome}</span>
															{openModal === 'client' &&
																selectedClients.some(c => c.idEntidade === entity.idEntidade) && (
																	<CheckCheck className="h-4 w-4 text-blue-600" />
																)}
														</CardTitle>
													</CardHeader>
													<CardContent className="p-3">
														<p className="text-xs">{formatCnpjCpf(entity?.cnpjCpf) || 'CPF/CNPJ não disponível'}</p>
														{entity?.logradouro && (
															<>
																<p className="text-xs">
																	{entity?.logradouro}, {entity?.numero || 'S/N'}
																</p>
																<p className="text-xs">
																	{entity?.cidade || 'Cidade não informada'}, {entity?.uf || 'UF'}
																</p>
															</>
														)}
													</CardContent>
												</Card>
											))}

											{/* Enhanced loading indicator for infinite scroll */}
											<div
												ref={loadingRef}
												className="flex items-center justify-center py-4"
											>
												{isLoadingMore ? (
													<div className="flex flex-col items-center gap-2">
														<Loader2 size={20} className="animate-spin text-primary" />
														<span className="text-sm text-muted-foreground">
															Carregando mais {openModal === 'promoter' ? 'promotores' : 'clientes'}...
														</span>
														{modalData.totalRecords > 0 && (
															<span className="text-xs text-gray-500">
																{modalData.entities.length} de {modalData.totalRecords} carregados
															</span>
														)}
													</div>
												) : modalData.hasMore ? (
													<div className="text-sm text-gray-400 flex flex-col items-center">
														<span>Role para baixo para carregar mais</span>
														{modalData.totalRecords > 0 && (
															<span className="text-xs mt-1">
																{modalData.entities.length} de {modalData.totalRecords} carregados ({getLoadPercentage()}%)
															</span>
														)}
													</div>
												) : (
													<div className="flex items-center justify-center py-4 border-t border-gray-200 w-full">
														<div className="text-center">
															<div className="text-sm text-gray-500 mb-1 flex items-center justify-center">
																<CheckCheck className="h-4 w-4 mr-1 text-green-500" />
																Todos os {openModal === 'promoter' ? 'promotores' : 'clientes'} foram carregados
															</div>
															<div className="text-xs text-gray-400">
																{modalData.totalRecords} {modalData.totalRecords === 1
																	? (openModal === 'promoter' ? 'promotor' : 'cliente')
																	: (openModal === 'promoter' ? 'promotores' : 'clientes')
																} no total
															</div>
														</div>
													</div>
												)}
											</div>
										</>
									) : (
										<div className="justify-center items-center h-full flex flex-col">
											<p className="text-gray-500">Lista vazia</p>
											<p className="text-gray-500">{modalData.emptyMessage}</p>
										</div>
									)}
								</div>
							</ScrollArea>

							{openModal === 'client' && selectedClients.length > 0 && (
								<div className="border-t pt-4">
									<div className="flex justify-between items-center">
										<p className="text-sm text-gray-600">
											{selectedClients.length} cliente(s) selecionado(s)
										</p>
										<Button onClick={handleCloseModal}>
											Concluir Seleção
										</Button>
									</div>
								</div>
							)}
						</DialogContent>
					</Dialog>
				)}
			</div>
		</div>
	)
}